import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { supabase } from '../supabaseClient'

const MISSION_NAME = 'Glenwood Op 9/3/26'

const CHARGES = [
  { key: 'fake_id', label: 'Fake ID' },
  { key: 'underage_possession', label: 'Underage Possession' },
  { key: 'attempt_enter_fraudulent_id', label: 'Attempt to Enter Using Fraudulent ID' },
  { key: 'attempt_purchase_fraudulent_id', label: 'Attempt to Purchase Using Fraudulent ID' },
  { key: 'underage_consumption', label: 'Underage Consumption' },
  { key: 'rdo', label: 'RDO' },
  { key: 'ch_90', label: 'CH 90' },
  { key: 'open_container', label: 'Open Container' },
  { key: 'weapons_offenses', label: 'Weapons Offenses' },
]

const TEAM_LABELS = {
  1: 'Team 1 — Wake / Meck',
  2: 'Team 2 — Wake / Meck',
  3: 'Team 3 — Raleigh PD',
  4: 'Team 4 — WCSO',
}

export default function MissionTally() {
  const navigate = useNavigate()
  const location = useLocation()
  const id = parseInt(location.pathname.split('/').pop())

  const [data, setData] = useState(null)
  const [notes, setNotes] = useState('')
  const [saving, setSaving] = useState(false)
  const [lastSaved, setLastSaved] = useState(null)
  const [notesTimer, setNotesTimer] = useState(null)
  const validId = !!TEAM_LABELS[id]

  useEffect(() => {
    if (!validId) { navigate('/mission'); return }
    // Initial fetch
    async function fetchData() {
      const { data: row } = await supabase
        .from('mission_teams')
        .select('*')
        .eq('mission_name', MISSION_NAME)
        .eq('team_id', id)
        .single()
      if (row) { setData(row); setNotes(row.notes || '') }
    }
    fetchData()

    // Realtime subscription
    const channel = supabase
      .channel(`mission_team_${id}`)
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'mission_teams',
        filter: `team_id=eq.${id}`,
      }, payload => {
        setData(payload.new)
        setNotes(prev => prev !== payload.new.notes ? payload.new.notes : prev)
      })
      .subscribe()

    return () => supabase.removeChannel(channel)
  }, [id, validId, navigate])

  const adjust = useCallback(async (field, delta) => {
    // Optimistic update
    setData(prev => prev ? { ...prev, [field]: Math.max(0, (prev[field] || 0) + delta) } : prev)
    await supabase.rpc('increment_mission_field', {
      p_mission_name: MISSION_NAME,
      p_team_id: id,
      p_field: field,
      p_delta: delta,
    })
  }, [id])

  const saveNotes = useCallback(async (value) => {
    setSaving(true)
    await supabase
      .from('mission_teams')
      .update({ notes: value, updated_at: new Date().toISOString() })
      .eq('mission_name', MISSION_NAME)
      .eq('team_id', id)
    setSaving(false)
    setLastSaved(new Date())
  }, [id])

  const handleNotesChange = (e) => {
    const val = e.target.value
    setNotes(val)
    clearTimeout(notesTimer)
    setNotesTimer(setTimeout(() => saveNotes(val), 1000))
  }

  if (!validId) return null

  const total = data ? CHARGES.reduce((s, c) => s + (data[c.key] || 0), 0) : 0

  return (
    <div style={{
      height: '100vh',
      display: 'flex',
      flexDirection: 'column',
      background: '#0f1f3d',
      fontFamily: "'Inter', system-ui, sans-serif",
      overflow: 'hidden',
    }}>
      {/* Header */}
      <div style={{
        background: 'rgba(0,0,0,0.3)',
        borderBottom: '1px solid rgba(61,100,148,0.3)',
        padding: '1rem 1.25rem',
        display: 'flex',
        alignItems: 'center',
        gap: '1rem',
        flexShrink: 0,
      }}>
        <button
          onClick={() => navigate('/mission')}
          style={{ background: 'none', border: 'none', color: '#8aadd4', fontSize: 22, cursor: 'pointer', padding: '4px 8px', lineHeight: 1 }}
        >
          ‹
        </button>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 11, color: '#8aadd4', letterSpacing: '0.1em', textTransform: 'uppercase' }}>{MISSION_NAME}</div>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#fff', marginTop: 1 }}>{TEAM_LABELS[id]}</div>
        </div>
        <div style={{
          background: 'rgba(61,100,148,0.3)',
          border: '1px solid rgba(61,100,148,0.5)',
          borderRadius: 8,
          padding: '4px 12px',
          textAlign: 'center',
        }}>
          <div style={{ fontSize: 20, fontWeight: 800, color: '#fff', lineHeight: 1 }}>{total}</div>
          <div style={{ fontSize: 10, color: '#8aadd4', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Total</div>
        </div>
      </div>

      {/* Charge rows */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '1rem 1.25rem', display: 'flex', flexDirection: 'column', gap: '0.625rem', paddingBottom: 'calc(6rem + env(safe-area-inset-bottom))' }}>
        {!data ? (
          <div style={{ color: 'rgba(255,255,255,0.4)', textAlign: 'center', padding: '3rem 0' }}>Loading...</div>
        ) : (
          CHARGES.map(charge => (
            <div key={charge.key} style={{
              background: 'rgba(61,100,148,0.15)',
              border: '1px solid rgba(61,100,148,0.25)',
              borderRadius: 10,
              padding: '0.875rem 1rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
            }}>
              <div style={{ flex: 1, fontSize: 15, fontWeight: 500, color: '#e2e8f0', lineHeight: 1.3 }}>
                {charge.label}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexShrink: 0 }}>
                <button
                  onClick={() => adjust(charge.key, -1)}
                  disabled={!data[charge.key]}
                  style={{
                    width: 44, height: 44,
                    borderRadius: 10,
                    border: '1px solid rgba(61,100,148,0.4)',
                    background: data[charge.key] ? 'rgba(61,100,148,0.3)' : 'rgba(61,100,148,0.1)',
                    color: data[charge.key] ? '#c8daf5' : 'rgba(255,255,255,0.2)',
                    fontSize: 24, fontWeight: 300,
                    cursor: data[charge.key] ? 'pointer' : 'not-allowed',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    lineHeight: 1, WebkitTapHighlightColor: 'transparent',
                  }}
                >
                  −
                </button>
                <div style={{
                  width: 40, textAlign: 'center',
                  fontSize: 24, fontWeight: 800,
                  color: data[charge.key] > 0 ? '#ffffff' : 'rgba(255,255,255,0.25)',
                  fontVariantNumeric: 'tabular-nums',
                }}>
                  {data[charge.key] || 0}
                </div>
                <button
                  onClick={() => adjust(charge.key, 1)}
                  style={{
                    width: 44, height: 44,
                    borderRadius: 10,
                    border: '1px solid rgba(61,100,148,0.5)',
                    background: 'rgba(61,100,148,0.35)',
                    color: '#c8daf5',
                    fontSize: 24, fontWeight: 300,
                    cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    lineHeight: 1, WebkitTapHighlightColor: 'transparent',
                  }}
                >
                  +
                </button>
              </div>
            </div>
          ))
        )}

        {/* Notes */}
        <div style={{ marginTop: '0.5rem' }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#8aadd4', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '0.5rem' }}>
            Other / Notes
          </div>
          <textarea
            value={notes}
            onChange={handleNotesChange}
            placeholder="Enter any additional notes..."
            rows={8}
            style={{
              width: '100%',
              background: 'rgba(61,100,148,0.15)',
              border: '1px solid rgba(61,100,148,0.25)',
              borderRadius: 10,
              padding: '0.875rem',
              color: '#e2e8f0',
              fontSize: 15,
              fontFamily: "'Inter', system-ui, sans-serif",
              resize: 'vertical',
              outline: 'none',
              boxSizing: 'border-box',
            }}
          />
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.25)', marginTop: 4, textAlign: 'right' }}>
            {saving ? 'Saving...' : lastSaved ? `Saved ${lastSaved.toLocaleTimeString()}` : ''}
          </div>
        </div>
      </div>
    </div>
  )
}
